/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import cns from 'classnames'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useQueryParams } from 'modules/router/hooks/useQueryParams'
import { useNFCSState } from 'modules/nfcs/hooks/useNFCSState'
import { useNFCSModal } from 'modules/nfcs/ui/NFCSModal/useNFCSModal'
import { useConnectWalletModal } from 'modules/wallet/ui/ConnectWalletModal'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'

import { Text } from 'shared/ui/common/Text'
import { HeaderNFCSButton } from '../HeaderNFCSButton'
import { useFirebase } from 'modules/analytics/firebase'
import { NFCS_HEADER_CLICK_EVENT } from 'modules/analytics/events'

import { ReactComponent as BoltSVG } from 'assets/bolt.svg'
import { ReactComponent as TimeSVG } from 'assets/time.svg'
import { ReactComponent as CloseSVG } from 'assets/close.svg'

import s from './HeaderNFCS.module.scss'

type Props = {
  className?: string
}

export function HeaderNFCS({ className }: Props) {
  const modalNFCS = useNFCSModal()
  const modalConnect = useConnectWalletModal()
  const { isWalletConnected } = useWeb3()
  const { nfcsStatus, nfcs } = useNFCSState()
  const { startnfcs } = useQueryParams()
  const navigate = useNavigate()
  const { trackEvent } = useFirebase()

  const handleOpen = () => {
    trackEvent(NFCS_HEADER_CLICK_EVENT, { status: nfcsStatus })
    modalNFCS.open({})
  }

  useEffect(() => {
    if (startnfcs) {
      if (!isWalletConnected) {
        modalConnect.open({})
      } else if (nfcsStatus === 'not-generated') {
        modalNFCS.open({})
        navigate({ search: '' })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startnfcs, isWalletConnected, nfcsStatus])

  if (!isWalletConnected) {
    return null
  }

  if (nfcsStatus === 'failed') {
    return (
      <HeaderNFCSButton
        isError
        icon={<CloseSVG />}
        onClick={handleOpen}
        className={className}
        text="Failed"
      />
    )
  }

  if (nfcsStatus === 'server-error') {
    return (
      <HeaderNFCSButton
        isError
        icon={<CloseSVG />}
        isLoading
        className={className}
        text={
          <>
            Server
            <br />
            error
          </>
        }
      />
    )
  }

  if (nfcsStatus === 'rate-limit') {
    return (
      <HeaderNFCSButton
        icon={<TimeSVG />}
        isLoading
        className={className}
        text={
          <>
            Retrieving
            <br />
            score...
          </>
        }
      />
    )
  }

  if (nfcsStatus === 'loading') {
    return (
      <HeaderNFCSButton
        icon={<TimeSVG />}
        isLoading
        className={className}
        text="Loading..."
      />
    )
  }

  if (nfcsStatus === 'not-generated') {
    return (
      <HeaderNFCSButton
        icon={<BoltSVG />}
        onClick={handleOpen}
        className={className}
        text={
          <>
            Generate <br />
            credit score
          </>
        }
      />
    )
  }

  if (nfcsStatus === 'generating') {
    return (
      <HeaderNFCSButton
        icon={<TimeSVG />}
        onClick={handleOpen}
        className={className}
        text="Generating..."
      />
    )
  }

  return (
    <div
      className={cns(s.wrap, s.generatedWrap, className)}
      onClick={handleOpen}
    >
      <BoltSVG className={s.generatedBolt} />
      <Text size={12} weight={500} isUppercased>
        My credit <br />
        score:
      </Text>
      <div className={s.line} />
      <Text size={24} weight={500} isUppercased>
        {nfcs}
      </Text>
    </div>
  )
}
